import React, { useEffect, useState, createRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { format, isSameDay } from 'ag-common/facades'

const TooltipPortal = ({ children }) => {
  const element = document.createElement("div");

  useEffect(() => {
    document.body.appendChild(element);
    return () => {
      document.body.removeChild(element);
    }
  }, [element]);

  return createPortal(children, element)
}


const Tooltip = ({ text, from, to, position, targetRef }) => {
  const [style, setStyle] = useState({});
  const calculateStyle = useCallback(() => {
    const clientRect = targetRef.current?.getBoundingClientRect();
    if (clientRect) {
      const topOffset = {
        [CalendarTooltip.Position.BOTTOM]: clientRect.height,
        [CalendarTooltip.Position.TOP]: 0,
      }
      const left = clientRect.left + clientRect.width / 2;
      const top = clientRect.top + topOffset[position];
      setStyle({
        left,
        top,
      })
    }
  }, [targetRef]);

  useEffect(() => {
    calculateStyle();
    window.addEventListener("scroll", calculateStyle, true);
    return () => {
      window.removeEventListener("scroll", calculateStyle, true);
    }
  }, [targetRef]);

  return (
    <div className={`calendar__tooltip calendar__tooltip--${position}`} style={style}>
      {text}
      <span className="calendar__tooltip__date">
        {from && to && isSameDay(from, to) && ` ${format(from, "eeeeee, dd.MM.yy")}`}
        {from && to && !isSameDay(from, to) && ` ${format(from, "eeeeee, dd.MM.yy")} - ${format(to, "eeeeee, dd.MM.yy")}`}
      </span>
    </div>
  )
}

const CalendarTooltip = ({ children, text, from, position, to }) => {
  const targetRef = createRef();

  const [isVisible, setIsVisible] = useState(false);

  const show = useCallback(() => setIsVisible(true), [setIsVisible]);
  const hide = useCallback(() => setIsVisible(false), [setIsVisible]);

  useEffect(() => {
    targetRef.current?.addEventListener("mouseenter", show);
    targetRef.current?.addEventListener("mouseleave", hide);
    return () => {
      targetRef.current?.removeEventListener("mouseenter", show);
      targetRef.current?.removeEventListener("mouseleave", hide);
    }
  }, [targetRef, show, hide])

  return (
    <>
      {children(targetRef)}
      {isVisible && (
        <TooltipPortal>
          <Tooltip text={text} from={from} to={to} position={position} targetRef={targetRef} />
        </TooltipPortal>
      )}
    </>
  )
}

CalendarTooltip.Position = {
  TOP: "top",
  BOTTOM: "bottom"
}

export default CalendarTooltip;
